import React from 'react';
import { Box, Chip, ChipGroup, Heading, Text, TextInput } from '@razorpay/blade/components';

import { deriveHoursPerDayMode } from 'components/Settings/AttendanceAndLeavesSettings/utils';

interface BaseDaysAndHoursProps {
  calculateLopOnWorkingDays: Boolean;
  baseHours?: string | null;
  isBaseHoursSaved: boolean;
  onCalculateLopOnWorkingDaysChange: (value: Boolean) => void;
  onBaseHoursChange: (value: string) => void;
}

export const BaseDaysAndHours = ({
  calculateLopOnWorkingDays,
  baseHours,
  isBaseHoursSaved,
  onCalculateLopOnWorkingDaysChange,
  onBaseHoursChange,
}: BaseDaysAndHoursProps) => {
  const hoursPerDayMode = deriveHoursPerDayMode(baseHours);

  const heading = isBaseHoursSaved ? 'Base days and hours' : 'Overtime Policy';
  const description = isBaseHoursSaved
    ? 'Used to convert monthly salary into daily and hourly rates for OT pay, LOP deductions, and final settlements.'
    : 'Set your base working hours. Required to enable automatic overtime calculation. Base days and hours will be used to convert monthly salary into daily and hourly rates for OT pay, LOP deductions, and final settlements.';

  return (
    <Box
      padding="spacing.6"
      borderRadius="medium"
      borderWidth="thin"
      borderColor="surface.border.gray.subtle"
      backgroundColor="surface.background.gray.intense">
      <Heading size="medium">{heading}</Heading>
      <Text marginTop="spacing.2" size="medium" color="surface.text.gray.muted">
        {description}
      </Text>

      <Box marginTop="spacing.6" display="flex" flexDirection="column" gap="spacing.5">
        <Box>
          <Text size="medium" color="surface.text.gray.muted">
            Working days per month
          </Text>
          <Box marginTop="spacing.3">
            <ChipGroup
              accessibilityLabel="Working days per month"
              selectionType="single"
              value={calculateLopOnWorkingDays ? "working-days" : "all-days"}
              onChange={({ values }) => onCalculateLopOnWorkingDaysChange(values[0] === "working-days")}>
              <Chip value="working-days">Working Days</Chip>
              <Chip value="all-days">All Days</Chip>
            </ChipGroup>
          </Box>
        </Box>

        <Box>
          <Text size="medium" color="surface.text.gray.muted">
            Hours Per Day
          </Text>
          <Box marginTop="spacing.3">
            <ChipGroup
              accessibilityLabel="Hours per day"
              selectionType="single"
              value={hoursPerDayMode}
              key={hoursPerDayMode}
              onChange={({ values }) => onBaseHoursChange(values[0] === 'custom' ? '' : values[0])}>
              <Chip value="8">8 Hours</Chip>
              <Chip value="9">9 Hours</Chip>
              <Chip value="custom">Custom</Chip>
            </ChipGroup>
          </Box>
          {hoursPerDayMode === 'custom' && (
            <Box marginTop="spacing.4" maxWidth="320px">
              <TextInput
                label=""
                accessibilityLabel="Custom hours per day"
                placeholder="e.g. 10"
                value={baseHours ?? ''}
                onChange={({ value }) => onBaseHoursChange(value ?? '')}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
